import { Hub } from 'aws-amplify/utils';
import { addDays, isAfter } from 'date-fns';
import { lazy, Suspense, useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useLocation } from 'react-router-dom';

import { AUTH_ROUTES } from '@/Constants';
import { usePtContext } from '@/contexts/PtContext';
import { useAqtQuery } from '@/hooks/useAqtQuery';
import { useMigration } from '@/hooks/useMigration';
import useLocalStorage from '@/utils/useLocalStorage';

const PromotionModal = lazy(() => import('./PromotionModal'));
const MigrationModal = lazy(() => import('./MigrationModal'));
const DownloadAppModal = lazy(() => import('./DownloadAppModal'));
const SignUpLiveModal = lazy(() => import('./SignUpLiveModal'));

type AppModalsProps = {
  cognitoId?: string;
};

export default function AppModals({ cognitoId }: AppModalsProps) {
  const { pathname } = useLocation();
  const { kycStatus } = usePtContext();
  const { isAllowMigration, openMigrationLink } = useMigration();
  const [isShowPromotion, setIsShowPromotion] = useState(false);
  const [isShowMigrationMsg, setIsShowMigrationMsg] = useState(false);

  const [isShowDownloadAppModalStorage, setIsDownloadAppModalStorage] = useLocalStorage('showDownloadAppModal', isMobile);
  const [isShowDownloadAppModal, setIsShowDownloadAppModal] = useState(isShowDownloadAppModalStorage);
  const shouldSuppressGlobalModal = [
    AUTH_ROUTES.login,
    AUTH_ROUTES.signup,
    AUTH_ROUTES.forgotPassword,
    AUTH_ROUTES.resetPassword,
    '/onboard',
  ].some(route => pathname.startsWith(route));
  const [isShowSignUpLiveModal, setIsShowSignUpLiveModal] = useState(false);
  const [shownPromotion, setShownPromotion] = useLocalStorage(
    'shownPromotionModal',
    {} as { checkedAt: string; publishedAt: string; shown: string[] }
  );

  const [isSignedIn, setIsSignedIn] = useState(false);
  const [checkPromotionTime, setCheckPromotionTime] = useState<Date>(() => {
    const today = new Date();
    today.setUTCHours(1, 0, 0, 0);
    return today;
  });

  const { data: promotionModalData, isSuccess } = useAqtQuery('promotionalModalContents', [], {
    enabled: !shownPromotion.checkedAt || isAfter(checkPromotionTime, new Date(shownPromotion.checkedAt)),
  });

  useEffect(() => {
    if (isAllowMigration) {
      const migrationModalCloseAt = localStorage.getItem('migrationModalCloseAt');

      const aWeekAfterLastCloseMigrationModal = migrationModalCloseAt && addDays(new Date(migrationModalCloseAt), 7).toISOString();

      const showMigrationMsg =
        !migrationModalCloseAt ||
        (aWeekAfterLastCloseMigrationModal && isAfter(new Date(), new Date(aWeekAfterLastCloseMigrationModal)));

      setIsShowMigrationMsg(!!showMigrationMsg);
    }
  }, [isAllowMigration]);

  useEffect(() => {
    if (!isSuccess) return;
    if (promotionModalData.totalElements > 0) {
      if (shownPromotion.publishedAt === promotionModalData.content[0].publishedAt)
        setShownPromotion(cur => ({ ...cur, checkedAt: new Date().toISOString() }));
      else
        setShownPromotion({
          checkedAt: new Date().toISOString(),
          publishedAt: promotionModalData.content[0].publishedAt,
          shown: [],
        });
    } else setShownPromotion({ checkedAt: new Date().toISOString(), publishedAt: '', shown: [] });
  }, [promotionModalData, isSuccess, shownPromotion.publishedAt, setShownPromotion]);

  useEffect(() => {
    if (pathname === '/' || pathname === '/discover/dashboard') {
      const newCheckPromotionTime = new Date();
      newCheckPromotionTime.setUTCHours(1, 0, 0, 0);
      if (isAfter(new Date(), newCheckPromotionTime)) setCheckPromotionTime(newCheckPromotionTime);
    }
  }, [pathname]);

  useEffect(() => {
    // Auth and onboarding routes should stay focused; global modals can resume after exit.
    if (
      cognitoId &&
      !shouldSuppressGlobalModal &&
      shownPromotion.publishedAt !== '' &&
      shownPromotion.shown &&
      !shownPromotion.shown.includes(cognitoId)
    ) {
      setIsShowPromotion(true);

      const shownList = [...(shownPromotion.shown ?? []), cognitoId];
      setShownPromotion({ ...shownPromotion, shown: shownList });
    }
  }, [shouldSuppressGlobalModal, cognitoId, shownPromotion, setShownPromotion]);

  useEffect(() => {
    setIsShowSignUpLiveModal(isSignedIn && ['NO_APPLICATION', 'REJECTED'].includes(kycStatus));
  }, [isSignedIn, kycStatus]);

  useEffect(() => {
    const listener = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signedIn') {
        setIsSignedIn(true);
      } else if (payload.event === 'signedOut') {
        setIsSignedIn(false);
      }
    });
    return listener;
  }, []);

  const handleMigrationOnClose = () => {
    localStorage.setItem('migrationModalCloseAt', new Date().toISOString());
    setIsShowMigrationMsg(false);
  };

  const shouldShowPromotionModal = isShowPromotion && !isShowDownloadAppModal && !isShowSignUpLiveModal;

  return (
    <Suspense fallback={null}>
      {!!promotionModalData?.totalElements && shouldShowPromotionModal && (
        <PromotionModal open data={promotionModalData.content[0]} onClose={() => setIsShowPromotion(false)} />
      )}

      {isShowMigrationMsg && (
        <MigrationModal onMigration={() => openMigrationLink()} open onClose={() => handleMigrationOnClose()} />
      )}

      {isShowDownloadAppModal && (
        <DownloadAppModal
          open
          handleShown={() => {
            setIsDownloadAppModalStorage(false);
          }}
          onClose={() => {
            setIsShowDownloadAppModal(false);
          }}
        />
      )}
      {isShowSignUpLiveModal && (
        <SignUpLiveModal
          onClose={() => {
            setIsShowSignUpLiveModal(false);
          }}
        />
      )}
    </Suspense>
  );
}
